// src/hooks/queries/usePatientInstancesByModality.js

import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePatientContext } from "../../classes/contexts/PatientContext";
import { MODALITY } from "../../classes/enums/MedicalImagesEnum";
import { GET_INSTANCES_BY_IDS_QUERY } from "../../queries/instanceQueries";
import { GET_INSTANCE_IDS_BY_SERIES_IDS_QUERY } from "../../queries/seriesQueries";
import { GET_STUDIES_LIST_BY_IDS_QUERY } from "../../queries/studiesQueries";
import { getInstanceThumbnail } from "../../services/InstancesService";

import { useRouteState } from "../useRouteState";

export const usePatientInstancesByModality = () => {
  const { patientData } = usePatientContext();
  const { params } = useRouteState();
  const client = useApolloClient();

  // 1) Fetch studies
  const studyIds = useMemo(
    () => patientData?.studies || [],
    [patientData?.studies]
  );
  const {
    data: studiesData,
    loading: studiesLoading,
    error: studiesError,
    refetch: refetchStudies,
  } = useQuery(GET_STUDIES_LIST_BY_IDS_QUERY, {
    variables: { ids: studyIds },
    skip: !studyIds.length,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // 2) Local state
  const [instancesByModality, setInstancesByModality] = useState({
    [MODALITY.DX]: [],
    [MODALITY.CT]: [],
    [MODALITY.MR]: [],
  });
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [instancesError, setInstancesError] = useState(null);

  useEffect(() => {
    if (studiesLoading) return;
    if (studiesError) {
      setInstancesError(studiesError);
      return;
    }

    const studies = studiesData?.studiesByIds || [];
    if (!studies.length) {
      setInstancesByModality({
        [MODALITY.DX]: [],
        [MODALITY.CT]: [],
        [MODALITY.MR]: [],
      });
      return;
    }

    let active = true;
    setLoadingInstances(true);
    setInstancesError(null);

    (async () => {
      try {
        // 3) Gather all series IDs
        const allSeriesIds = studies.flatMap(s => s.series_ids || []);
        if (!allSeriesIds.length) {
          if (active)
            setInstancesByModality({
              [MODALITY.DX]: [],
              [MODALITY.CT]: [],
              [MODALITY.MR]: [],
            });
          return;
        }

        // 4) Fetch series → instance_ids
        const { data: seriesData } = await client.query({
          query: GET_INSTANCE_IDS_BY_SERIES_IDS_QUERY,
          variables: { ids: allSeriesIds },
        });
        const seriesList = seriesData.seriesByIds || [];

        // 5) Build lookup maps
        const seriesInstanceMap = {}; // series_id → [instanceIds]
        const instanceToSeriesMap = {}; // instanceId → series_id
        seriesList.forEach(s => {
          const sid = s._id;
          const instIds = s.instance_ids || [];
          seriesInstanceMap[sid] = instIds;
          instIds.forEach(iid => {
            instanceToSeriesMap[iid] = sid;
          });
        });

        // 6) Fetch full instance objects
        const allInstanceIds = seriesList.flatMap(s => s.instance_ids || []);
        if (!allInstanceIds.length) {
          if (active)
            setInstancesByModality({
              [MODALITY.DX]: [],
              [MODALITY.CT]: [],
              [MODALITY.MR]: [],
            });
          return;
        }
        const { data: instancesData } = await client.query({
          query: GET_INSTANCES_BY_IDS_QUERY,
          variables: { ids: allInstanceIds },
        });
        const instances = instancesData.instancesByIds || [];
        const patientId = params.patientId;
        const patientInstances = patientId
          ? instances.filter(inst => inst.patient_id === patientId)
          : instances;

        // 7) Enrich each instance
        const enriched = await Promise.all(
          patientInstances.map(async inst => {
            let url = null;
            let thumbnailError = false;
            try {
              const resp = await getInstanceThumbnail(
                params.patientId,
                inst._id
              );
              if (resp && resp.data) {
                url = URL.createObjectURL(resp.data);
              } else {
                thumbnailError = true;
              }
            } catch (err) {
              thumbnailError = true;
            }

            const seriesId = instanceToSeriesMap[inst._id] || null;

            return {
              ...inst,
              url,
              thumbnailError,
              series_id: seriesId,
              originalModality: inst.modality,
              modality: inst.modality === "CR" ? "DX" : inst.modality,
              orientation: inst.orientation,
              date: inst.creation_date,
              title: inst.file_name,
              patient_id: inst.patient_id || params.patientId,
            };
          })
        );

        // 8) Group by modality & sort by date desc
        const grouped = {
          [MODALITY.DX]: [],
          [MODALITY.CT]: [],
          [MODALITY.MR]: [],
        };
        enriched.forEach(i => {
          if (grouped[i.modality]) grouped[i.modality].push(i);
        });
        Object.values(grouped).forEach(arr =>
          arr.sort((a, b) => new Date(b.date) - new Date(a.date))
        );

        if (active) setInstancesByModality(grouped);
      } catch (err) {
        if (active) setInstancesError(err);
      } finally {
        if (active) setLoadingInstances(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [studiesData, studiesLoading, studiesError, client, params.patientId]);

  const refetchData = useCallback(async () => {
    try {
      await client.refetchQueries({
        include: [
          GET_STUDIES_LIST_BY_IDS_QUERY,
          GET_INSTANCE_IDS_BY_SERIES_IDS_QUERY,
          GET_INSTANCES_BY_IDS_QUERY,
        ],
      });

      if (studyIds.length > 0) {
        await refetchStudies();
      }
    } catch (error) {
      setInstancesError(error);
    }
  }, [client, studyIds, refetchStudies]);

  // Update display settings for a specific instance in local state
  const updateLocalDisplaySettings = useCallback(
    (instanceId, viewType, brightness, contrast) => {
      setInstancesByModality(prev => {
        const updated = { ...prev };
        for (const modality of Object.keys(updated)) {
          updated[modality] = updated[modality].map(inst => {
            if (inst._id === instanceId) {
              return {
                ...inst,
                display_settings: {
                  ...inst.display_settings,
                  [viewType]: { brightness, contrast },
                },
              };
            }
            return inst;
          });
        }
        return updated;
      });
    },
    []
  );

  return {
    instancesByModality,
    loading: studiesLoading || loadingInstances,
    error: studiesError || instancesError,
    refetch: refetchData,
    updateLocalDisplaySettings,
  };
};
